/**
 * GH-268 — TourAnchorMarker.
 *
 * Renders a small ✓ checkmark badge in the top-right corner of a previously
 * visited tour anchor. Lives in the same fixed overlay layer as the spotlight
 * so positioning is in viewport coords. Multiple markers stack — one per
 * visited step — and only render for steps that are NOT the currently active
 * step (the active step gets the spotlight ring instead).
 *
 * The badge is purely visual (no pointer events, aria-hidden); it signals
 * "you've been here" without intercepting clicks on the underlying surface.
 */

import { Check } from 'lucide-react'

interface TourAnchorMarkerProps {
  /** Bounding rect of the visited anchor (viewport coords). */
  rect: { top: number; left: number; width: number; height: number }
}

const SIZE = 22 // px — diameter of the badge

export function TourAnchorMarker({ rect }: TourAnchorMarkerProps) {
  // Anchor at top-right of the bounding box, slightly outside so it doesn't
  // overlap content inside the anchor.
  const top = rect.top - SIZE / 2
  const left = rect.left + rect.width - SIZE / 2

  return (
    <div
      aria-hidden
      className="pointer-events-none absolute flex items-center justify-center rounded-full bg-brand-500 text-white shadow-lg ring-2 ring-white/80 dark:ring-surface-1"
      style={{
        top,
        left,
        width: SIZE,
        height: SIZE,
        transition:
          'top 360ms cubic-bezier(0.16, 1, 0.3, 1), left 360ms cubic-bezier(0.16, 1, 0.3, 1)',
      }}
    >
      <Check className="h-3 w-3" strokeWidth={3} />
    </div>
  )
}
